import axios from 'axios';
import type { CacheRequestConfig } from 'axios-cache-interceptor';
import type { LevelCriteria } from '../pages/new-designs/tabs/capability-setup/capabilitySetup';
import type { CapabilityLevel } from '../pages/new-designs/tabs/Program/components/learning-lifecycle/teamCapability';

// Hiring (DA-5042): the backend in Dosen-v2-BE src/service/hiring. Two halves
// that share nothing: the Team Owner's (rounds, candidates, analysis,
// transcripts, ranking, hire, close) and the candidate's own, which is the
// only thing a candidate can reach at all (the backend enforces an endpoint
// allow-list; see PRD 6.1 / C-9).
//
// Authentication and the region go on every request through the app's axios
// interceptor (App.tsx). `orgId` names the org for platform admins only;
// everyone else's token carries it.

const base = () => `${process.env.REACT_APP_API}`;
const query = (params: Record<string, string | undefined>) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`)
    .join('&');
  return qs ? `?${qs}` : '';
};
const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data);
// React Query is these reads' cache (src/queries/useHiring.ts). The shared axios
// also runs axios-cache-interceptor (api/index.ts), which answers a GET from
// memory for 5 minutes, so a refetch after an invite, a grade or a hire would
// get the answer from before it.
const fresh: CacheRequestConfig = { cache: false };

// ─── Shapes: the owner's side ───────────────────────────────────────────────

export type RoundStatus = 'draft' | 'open' | 'closed';
export type CandidateStatus = 'invited' | 'started' | 'completed' | 'hired' | 'unsuccessful' | 'removed' | 'expired';

/** A block of the wording that is under legal review (PRD 8.3, 8.4). */
export interface CopyBlock {
  key: string;
  version: string;
  title: string;
  /** Lines, with **bold** spans. */
  lines: string[];
}

export interface HiringRoleView {
  id: string;
  name: string;
  teamId: string;
  teamName: string;
  /** Only a role whose capabilities and levels are confirmed can be measured (H-4). */
  ready: boolean;
  capabilities: Array<{ id: string; name: string; criteria: LevelCriteria }>;
}

export interface HiringAccessView {
  canOpen: boolean;
  orgName: string;
  acknowledgement: { version: string; at: number } | null;
  /** L-11: until this is confirmed the owner may look but not act. */
  acknowledgementRequired: boolean;
  ownerAcknowledgement: CopyBlock;
  rationaleTooltip: CopyBlock;
  roles: HiringRoleView[];
  blockedRoleCountries: string[];
  blockedRoleMessage: string;
}

export interface HiringItemView {
  capabilityId: string;
  capabilityName: string;
  levels: Record<string, string>;
  roleplay: string;
  /** What Elliott drafted, so an edit can be spotted and undone. */
  draftRoleplay: string;
  /** Elliott's view of an edited roleplay: can it still be graded on the four levels? */
  roleplayCheck: { status: 'passed' | 'flagged' | 'unchecked'; reason: string; text: string } | null;
  quiz: { question: string; options: string[]; correct: number };
}

export interface HiringCounts {
  invited: number;
  started: number;
  completed: number;
  hired: number;
  unsuccessful: number;
  removed: number;
  expired: number;
}

export interface HiringRoundView {
  id: string;
  name: string;
  roleId: string;
  roleName: string;
  teamId: string;
  teamName: string;
  hiringLocation: { country: string; state: string };
  retentionMonths: number;
  /** A legal minimum and a legal maximum conflict on this round (PRD 8.1b). */
  retentionNeedsLegalReview: boolean;
  contactEmail: string;
  welcomeMessage: string;
  /** What Elliott drafts the Roleplays from (DA-5090); locked once the round opens. */
  jobDescription: string;
  /** The auto Notes as the candidate will read them, built from the settings (H-8). */
  notes: { version: string; title: string; lines: string[] };
  defaultInputMode: 'text' | 'voice';
  attempts: number;
  fields: Array<{ key: string; label: string; required: boolean }>;
  items: HiringItemView[];
  status: RoundStatus;
  counts: HiringCounts;
  ranking: string[];
  rankingVersion: number;
  rankedByName: string;
  rankedAt: number | null;
  openedAt: number | null;
  closedAt: number | null;
  closeRationale: string;
  createdAt: number;
  createdByName: string;
  readOnly: boolean;
}

export interface HiringCandidateCapabilityView {
  capabilityId: string;
  capabilityName: string;
  level: CapabilityLevel | null;
  /** 2 to 3 lines for the hiring manager (PRD 7.2). */
  analysis: string;
  /** The CV against this capability (PRD 7.3). */
  cvAnalysis: string;
  bestAttemptNumber: number;
  attemptsUsed: number;
  /** How the attempt that reached this level was answered. Context, never a score (L-6). */
  inputMode: 'text' | 'voice' | null;
  quiz: { answerIndex: number | null; passed: boolean | null };
  /** H-25, totalled over this capability's answers. Context, never a score. */
  signals: { pasteCount: number; copyCount: number; tabSwitches: number };
}

/** One of the team's notes on a candidate, who never sees them (H-16). */
export interface HiringNoteView {
  id: string;
  text: string;
  addedByName: string;
  addedAt: number;
  editedAt: number | null;
}

export interface HiringCandidateView {
  id: string;
  roundId: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  status: CandidateStatus;
  rank: number | null;
  progress: number;
  residence: { country: string; state: string } | null;
  noticeVersion: string | null;
  noticeAt: number | null;
  answers: Record<string, string>;
  linkedinUrl: string;
  hasCv: boolean;
  cvFileName: string;
  invitedAt: number;
  startedAt: number | null;
  completedAt: number | null;
  removedAt: number | null;
  hiredAt: number | null;
  workEmail: string;
  workEmailVerified: boolean;
  /** Already sent the unsuccessful email, so a later close will not send it again. */
  toldUnsuccessful: boolean;
  /** Newest first. */
  notes: HiringNoteView[];
  capabilities: HiringCandidateCapabilityView[];
  retention: string;
}

export interface HiringTranscriptView {
  candidate: { id: string; name: string };
  /** L-6: the caption that goes with the integrity signals wherever they show. */
  integrityCaption: string;
  capabilities: Array<{
    capabilityId: string;
    capabilityName: string;
    situation: string;
    level: CapabilityLevel | null;
    exchanges: Array<{
      from: 'candidate' | 'elliott';
      text: string;
      /** A question, which used no attempt (PRD 7.1). */
      clarification: boolean;
      level: CapabilityLevel | null;
      inputMode: 'text' | 'voice' | null;
      responseSeconds: number | null;
      /** H-25, context only: recorded, shown, and never part of a level. */
      pasteCount: number;
      tabSwitches: number;
      copyCount: number;
      at: number;
    }>;
    quiz: { question: string; options: string[]; correct: number; answerIndex: number | null };
  }>;
}

export interface HiringActivityEntry {
  action: string;
  actorName: string;
  actorKind: 'owner' | 'candidate' | 'system';
  candidateId: string;
  rationale: string;
  at: number;
}

// ─── Shapes: the candidate's side ───────────────────────────────────────────

export type CandidateStep =
  | 'residence'
  | 'notice'
  | 'onboarding'
  | 'welcome'
  | 'assessment'
  | 'done'
  | 'blocked'
  | 'closed';

export interface CandidateItemView {
  capabilityId: string;
  capabilityName: string;
  /** Withheld until they start it, so it cannot be read ahead. */
  situation: string | null;
  attemptsAllowed: number;
  attemptsUsed: number;
  roleplayDone: boolean;
  quizDone: boolean;
  /** Only sent once the Roleplay is done, so it cannot hint at the answer. */
  quiz: { question: string; options: string[] } | null;
  exchanges: Array<{ from: 'candidate' | 'elliott'; text: string; clarification: boolean; at: number }>;
}

export interface CandidateMeView {
  /** True only for an actual candidate; see `CandidateMeAnswer`. */
  candidate: true;
  step: CandidateStep;
  orgName: string;
  roleName: string;
  roundName: string;
  firstName: string;
  contactEmail: string;
  retention: string;
  defaultInputMode: 'text' | 'voice';
  attempts: number;
  welcomeMessage: string;
  notes: { version: string; title: string; lines: string[] } | null;
  notice: { version: string; title: string; lines: string[] } | null;
  fields: Array<{ key: string; label: string; required: boolean }>;
  answers: Record<string, string>;
  linkedinUrl: string;
  hasCv: boolean;
  cvFileName: string;
  items: CandidateItemView[];
  blockedMessage: string | null;
}

/**
 * What GET /v3/hiring/me answers. Anyone signed in may ask whether they are a
 * candidate, so a non-candidate gets `{ candidate: false }` and a 200, not a
 * 403: the question is about their own account, and the seven other candidate
 * routes are the ones that refuse everybody else.
 */
export type CandidateMeAnswer = CandidateMeView | { candidate: false };

export const isCandidate = (answer: CandidateMeAnswer | undefined): answer is CandidateMeView =>
  Boolean(answer?.candidate);

/**
 * The backend refuses a candidate everything but their own assessment (PRD
 * 6.1, C-9), and the app's startup calls one of those refused endpoints —
 * `userBasicSettings`, the member first-time setup's own — before it knows who
 * anyone is. For a candidate that refusal is not a crash, it is the answer, so
 * it carries a reason the app can act on.
 *
 * Found on 26 Oct 2026 by walking the flow by hand: the first candidate to
 * sign in reached "This account can only complete its assessment" with a Back
 * to Dosen button that brought them straight back to it. They could not get to
 * their assessment at all.
 */
export const CANDIDATE_REASON = 'hiring_candidate';

export const isCandidateRefusal = (error: unknown): boolean => {
  const res = (error as { response?: { status?: number; data?: { reason?: string } } })?.response;
  return res?.status === 403 && res?.data?.reason === CANDIDATE_REASON;
};

/**
 * Remembered for this tab only, and only as a reason to ASK. The landing still
 * takes the server's own answer (GET /v3/hiring/me) as the truth and forgets
 * this as soon as the server says they are not a candidate — a hired candidate
 * becomes an ordinary member, so nothing about this may outlive their session.
 */
const REFUSED_KEY = 'hiring.refusedAsCandidate';

export const rememberCandidateRefusal = (): void => {
  try {
    sessionStorage.setItem(REFUSED_KEY, '1');
  } catch {
    /* storage can be refused; the landing then waits for the profile instead */
  }
};

export const forgetCandidateRefusal = (): void => {
  try {
    sessionStorage.removeItem(REFUSED_KEY);
  } catch {
    /* noop */
  }
};

export const wasRefusedAsCandidate = (): boolean => {
  try {
    return sessionStorage.getItem(REFUSED_KEY) === '1';
  } catch {
    return false;
  }
};

export interface CandidateTurnView {
  /** A question costs no attempt; an answer uses one (PRD 7.1). */
  kind: 'clarification' | 'answer';
  reply: string;
  attemptsUsed: number;
  attemptsAllowed: number;
  finished: boolean;
}

// ─── Calls ──────────────────────────────────────────────────────────────────

export interface HiringOrgProfile {
  website: string;
  text: string;
  draftedAt: number | null;
  approvedAt: number | null;
  approvedBy: string;
}

export const hiringApi = {
  // The owner's.
  access: (orgId?: string) => data<HiringAccessView>(axios.get(`${base()}/v3/hiring/access${query({ orgId })}`, fresh)),
  acknowledge: (version: string, orgId?: string) =>
    data<{ version: string; at: number }>(
      axios.post(`${base()}/v3/hiring/acknowledgement${query({ orgId })}`, { version })
    ),
  rounds: (filter: { status?: string; roleId?: string } = {}, orgId?: string) =>
    data<{ rounds: HiringRoundView[] }>(axios.get(`${base()}/v3/hiring/rounds${query({ ...filter, orgId })}`, fresh)),
  round: (roundId: string, orgId?: string) =>
    data<HiringRoundView>(axios.get(`${base()}/v3/hiring/rounds/${roundId}${query({ orgId })}`, fresh)),
  createRound: (
    body: {
      name: string;
      roleId: string;
      hiringLocation: { country: string; state?: string };
      restrictedLocation: boolean;
      contactEmail: string;
      jobDescription?: string;
      saveJobDescriptionToRole?: boolean;
    },
    orgId?: string
  ) => data<HiringRoundView>(axios.post(`${base()}/v3/hiring/rounds${query({ orgId })}`, body)),
  // DA-5090: the job description and the organisation profile Elliott drafts from.
  roleJobDescription: (roleId: string, orgId?: string) =>
    data<{ roleId: string; jobDescription: string }>(
      axios.get(`${base()}/v3/hiring/roles/${encodeURIComponent(roleId)}/job-description${query({ orgId })}`, fresh)
    ),
  extractJobDescription: (file: { fileBase64: string; fileName: string; contentType: string }, orgId?: string) =>
    data<{ text: string; truncated: boolean }>(
      axios.post(`${base()}/v3/hiring/job-description/extract${query({ orgId })}`, file)
    ),
  orgProfile: (orgId?: string) => data<HiringOrgProfile>(axios.get(`${base()}/v3/hiring/org-profile${query({ orgId })}`, fresh)),
  draftOrgProfile: (website: string, orgId?: string) =>
    data<{ website: string; text: string; pagesRead: string[] }>(
      axios.post(`${base()}/v3/hiring/org-profile/draft${query({ orgId })}`, { website })
    ),
  saveOrgProfile: (body: { website?: string; text: string }, orgId?: string) =>
    data<HiringOrgProfile>(axios.put(`${base()}/v3/hiring/org-profile${query({ orgId })}`, body)),
  updateRound: (roundId: string, body: Record<string, unknown>, orgId?: string) =>
    data<HiringRoundView>(axios.patch(`${base()}/v3/hiring/rounds/${roundId}${query({ orgId })}`, body)),
  buildAssessment: (roundId: string, capabilityIds: string[], orgId?: string) =>
    data<HiringRoundView>(
      axios.post(`${base()}/v3/hiring/rounds/${roundId}/assessment${query({ orgId })}`, { capabilityIds })
    ),
  draftItem: (roundId: string, capabilityId: string, orgId?: string) =>
    data<HiringItemView>(
      axios.post(
        `${base()}/v3/hiring/rounds/${roundId}/assessment/items/${encodeURIComponent(capabilityId)}${query({ orgId })}`,
        {}
      )
    ),
  openRound: (roundId: string, orgId?: string) =>
    data<HiringRoundView>(axios.post(`${base()}/v3/hiring/rounds/${roundId}/open${query({ orgId })}`, {})),
  closeRound: (roundId: string, rationale: string, orgId?: string) =>
    data<HiringRoundView>(axios.post(`${base()}/v3/hiring/rounds/${roundId}/close${query({ orgId })}`, { rationale })),
  reopenRound: (roundId: string, orgId?: string) =>
    data<HiringRoundView>(axios.post(`${base()}/v3/hiring/rounds/${roundId}/reopen${query({ orgId })}`, {})),
  activity: (roundId: string, orgId?: string) =>
    data<{ entries: HiringActivityEntry[] }>(
      axios.get(`${base()}/v3/hiring/rounds/${roundId}/activity${query({ orgId })}`, fresh)
    ),
  setRanking: (roundId: string, order: string[], version: number, orgId?: string) =>
    data<{ ranking: string[]; version: number; rankedByName: string; rankedAt: number }>(
      axios.put(`${base()}/v3/hiring/rounds/${roundId}/ranking${query({ orgId })}`, { order, version })
    ),
  candidates: (roundId: string, orgId?: string) =>
    data<{ candidates: HiringCandidateView[] }>(
      axios.get(`${base()}/v3/hiring/rounds/${roundId}/candidates${query({ orgId })}`, fresh)
    ),
  invite: (
    roundId: string,
    candidates: Array<{ firstName: string; lastName?: string; email: string }>,
    orgId?: string
  ) =>
    data<{ invited: HiringCandidateView[]; refused: Array<{ email: string; why: string }> }>(
      axios.post(`${base()}/v3/hiring/rounds/${roundId}/candidates${query({ orgId })}`, { candidates })
    ),
  candidate: (candidateId: string, orgId?: string) =>
    data<HiringCandidateView>(axios.get(`${base()}/v3/hiring/candidates/${candidateId}${query({ orgId })}`, fresh)),
  addNote: (candidateId: string, text: string, orgId?: string) =>
    data<HiringCandidateView>(
      axios.post(`${base()}/v3/hiring/candidates/${candidateId}/notes${query({ orgId })}`, { text })
    ),
  editNote: (candidateId: string, noteId: string, text: string, orgId?: string) =>
    data<HiringCandidateView>(
      axios.patch(`${base()}/v3/hiring/candidates/${candidateId}/notes/${noteId}${query({ orgId })}`, { text })
    ),
  deleteNote: (candidateId: string, noteId: string, orgId?: string) =>
    data<HiringCandidateView>(
      axios.delete(`${base()}/v3/hiring/candidates/${candidateId}/notes/${noteId}${query({ orgId })}`)
    ),
  transcript: (candidateId: string, orgId?: string) =>
    data<HiringTranscriptView>(
      axios.get(`${base()}/v3/hiring/candidates/${candidateId}/transcript${query({ orgId })}`, fresh)
    ),
  compare: (roundId: string, ids: string[], orgId?: string) =>
    data<{ capabilities: Array<{ capabilityId: string; capabilityName: string }>; candidates: HiringCandidateView[] }>(
      axios.get(`${base()}/v3/hiring/rounds/${roundId}/compare${query({ ids: ids.join(','), orgId })}`, fresh)
    ),
  resendInvite: (candidateId: string, orgId?: string) =>
    data<{ ok: true }>(axios.post(`${base()}/v3/hiring/candidates/${candidateId}/resend${query({ orgId })}`, {})),
  revokeInvite: (candidateId: string, orgId?: string) =>
    data<{ ok: true }>(axios.post(`${base()}/v3/hiring/candidates/${candidateId}/revoke${query({ orgId })}`, {})),
  removeCandidate: (candidateId: string, orgId?: string) =>
    data<HiringCandidateView>(
      axios.post(`${base()}/v3/hiring/candidates/${candidateId}/remove${query({ orgId })}`, {})
    ),
  restoreCandidate: (candidateId: string, orgId?: string) =>
    data<HiringCandidateView>(
      axios.post(`${base()}/v3/hiring/candidates/${candidateId}/restore${query({ orgId })}`, {})
    ),
  // H-22: the typed confirmation travels with the call, so nothing can delete a
  // candidate by accident.
  deleteCandidate: (candidateId: string, orgId?: string) =>
    data<{ deleted: true }>(
      axios.delete(`${base()}/v3/hiring/candidates/${candidateId}${query({ confirm: 'DELETE', orgId })}`)
    ),
  hire: (
    candidateId: string,
    body: { rationale: string; workEmail: string; teamId?: string; roleId?: string; access: 'learner' | 'admin' },
    orgId?: string
  ) =>
    data<HiringCandidateView>(
      axios.post(`${base()}/v3/hiring/candidates/${candidateId}/hire${query({ orgId })}`, body)
    ),
  correctWorkEmail: (candidateId: string, workEmail: string, orgId?: string) =>
    data<HiringCandidateView>(
      axios.put(`${base()}/v3/hiring/candidates/${candidateId}/work-email${query({ orgId })}`, { workEmail })
    ),
  /** The CV and the explanation are files: fetched as blobs and handed to the browser. */
  cvUrl: (candidateId: string, orgId?: string) => `${base()}/v3/hiring/candidates/${candidateId}/cv${query({ orgId })}`,
  explanation: (candidateId: string, orgId?: string) =>
    data<Record<string, unknown>>(
      axios.get(`${base()}/v3/hiring/candidates/${candidateId}/explanation${query({ orgId })}`, fresh)
    ),

  // The candidate's own.
  me: () => data<CandidateMeAnswer>(axios.get(`${base()}/v3/hiring/me`, fresh)),
  setResidence: (body: { country: string; state?: string }) =>
    data<CandidateMeView>(axios.put(`${base()}/v3/hiring/me/residence`, body)),
  acknowledgeNotice: (version: string, consents: string[] = []) =>
    data<CandidateMeView>(axios.post(`${base()}/v3/hiring/me/notice`, { version, consents })),
  setOnboarding: (body: { answers?: Record<string, string>; linkedinUrl?: string }) =>
    data<CandidateMeView>(axios.put(`${base()}/v3/hiring/me/onboarding`, body)),
  uploadCv: (body: { fileBase64: string; fileName: string; contentType: string }) =>
    data<CandidateMeView>(axios.post(`${base()}/v3/hiring/me/cv`, body)),
  start: () => data<CandidateMeView>(axios.post(`${base()}/v3/hiring/me/start`, {})),
  answerRoleplay: (
    capabilityId: string,
    body: {
      response: string;
      inputMode: 'text' | 'voice';
      presentedAt?: number;
      pasteCount?: number;
      tabSwitches?: number;
      copyCount?: number;
    }
  ) =>
    data<CandidateTurnView>(
      axios.post(`${base()}/v3/hiring/me/items/${encodeURIComponent(capabilityId)}/roleplay`, body)
    ),
  answerQuiz: (capabilityId: string, answerIndex: number) =>
    data<{ answered: true; assessmentComplete: boolean }>(
      axios.post(`${base()}/v3/hiring/me/items/${encodeURIComponent(capabilityId)}/quiz`, { answerIndex })
    )
};

/** What the backend said went wrong, as the screens show it. */
export const hiringMessage = (err: unknown, fallback = 'Something went wrong'): string => {
  const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || (err as Error)?.message || fallback;
};
